import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Calendar,
    Users,
    Activity,
    Star,
    ArrowRight,
    Check,
    Coffee,
    LineChart,
    Clock,
    Flame,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

function fmtItDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

const HERO_IMG =
    "https://images.unsplash.com/photo-1754887966092-4ed8f305c78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxidXN5JTIwbW9kZXJuJTIwcmVzdGF1cmFudCUyMGludGVyaW9yfGVufDB8fHx8MTc3ODQzODIwN3ww&ixlib=rb-4.1.0&q=85";

const FEATURE_IMG =
    "https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwa2l0Y2hlbiUyMGNoZWYlMjB3b3JraW5nfGVufDB8fHx8MTc3ODQzODIwN3ww&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
    const { user } = useAuth();
    const [plans, setPlans] = useState(null);

    useEffect(() => {
        api.get("/pricing/public")
            .then(({ data }) => setPlans(data.plans))
            .catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-sf-bg" data-testid="landing-page">
            {/* Nav */}
            <header className="glass-nav sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-between">
                    <Logo className="h-28 md:h-36 w-auto" />
                    <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-sf-secondary">
                        <a href="#features" data-testid="nav-features">
                            Funzionalità
                        </a>
                        <a href="#pricing" data-testid="nav-pricing">
                            Prezzi
                        </a>
                        <a href="#brigade" data-testid="nav-brigade">
                            Brigata
                        </a>
                        <Link to="/istruzioni" data-testid="nav-instructions">
                            Istruzioni
                        </Link>
                    </nav>
                    <div className="flex items-center gap-2">
                        {user ? (
                            <Link to="/app">
                                <Button data-testid="nav-app-btn">
                                    Apri App
                                </Button>
                            </Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="hidden sm:inline-flex"
                                >
                                    <Button
                                        variant="ghost"
                                        data-testid="nav-login-btn"
                                    >
                                        Accedi
                                    </Button>
                                </Link>
                                <Link
                                    to="/portal-login"
                                    className="hidden sm:inline-flex"
                                >
                                    <Button
                                        variant="ghost"
                                        data-testid="nav-portal-login-btn"
                                        className="text-amber-brand hover:bg-amber-brand/10"
                                    >
                                        Sono dipendente
                                    </Button>
                                </Link>
                                <Link to="/register">
                                    <Button data-testid="nav-register-btn">
                                        Inizia gratis
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="hero-mesh relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7 reveal">
                        <div className="label-eyebrow mb-6">
                            La sala, in un'unica regia
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6 text-navy">
                            Turni, performance e
                            <span className="italic text-amber-brand">
                                {" "}
                                produttività di brigata
                            </span>
                            .
                        </h1>
                        <p className="text-lg text-sf-secondary max-w-xl mb-10 leading-relaxed">
                            Staff Flow 360 dà ai ristoratori italiani una sola
                            piattaforma per gestire camerieri, calendario
                            turni, KPI di sala e produttività di brigata.
                            Ordinato, veloce, fatto su misura per il servizio.
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                            <Link to="/register">
                                <Button
                                    size="lg"
                                    className="bg-navy hover:bg-navy/90 text-white px-8 lift"
                                    data-testid="hero-cta-register"
                                >
                                    Prova 14 giorni gratis{" "}
                                    <ArrowRight
                                        size={16}
                                        className="ml-2"
                                    />
                                </Button>
                            </Link>
                            <Link to="#pricing">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-navy text-navy hover:bg-navy/5"
                                    data-testid="hero-cta-pricing"
                                >
                                    Vedi i piani
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-12 flex items-center gap-8 text-xs text-sf-muted">
                            <div className="flex items-center gap-2">
                                <Check size={14} className="text-amber-brand" />{" "}
                                Setup in 5 minuti
                            </div>
                            <div className="flex items-center gap-2">
                                <Check size={14} className="text-amber-brand" />{" "}
                                Stripe, sicuro
                            </div>
                            <div className="flex items-center gap-2">
                                <Check size={14} className="text-amber-brand" />{" "}
                                Made in Italy
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-5 reveal" style={{ animationDelay: "120ms" }}>
                        <div className="relative">
                            <div className="absolute -inset-4 bg-amber-brand/15 rounded-3xl rotate-2"></div>
                            <img
                                src={HERO_IMG}
                                alt="Ristorante moderno"
                                className="relative rounded-2xl border border-sf shadow-[0_20px_50px_rgba(10,35,66,0.15)] w-full object-cover h-[420px]"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-white border border-sf rounded-xl p-4 shadow-lg w-[230px]">
                                <div className="label-eyebrow mb-1">
                                    Top performer
                                </div>
                                <div className="font-display text-2xl text-navy">
                                    Marco R.
                                </div>
                                <div className="text-xs text-sf-muted">
                                    142 coperti · €38,40 medio
                                </div>
                            </div>
                            <div className="absolute -top-5 -right-5 bg-navy text-white rounded-xl p-4 shadow-lg w-[180px]">
                                <div className="text-xs uppercase tracking-widest opacity-80 mb-1">
                                    Settimana
                                </div>
                                <div className="font-display text-3xl">
                                    214h
                                </div>
                                <div className="text-xs opacity-70">
                                    pianificate
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features bento */}
            <section
                id="features"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-24"
            >
                <div className="grid md:grid-cols-2 gap-10 mb-14 items-end">
                    <div>
                        <div className="label-eyebrow mb-3">
                            Cosa fa Staff Flow
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl text-navy leading-tight">
                            Una sola regia per
                            <br />
                            <span className="italic text-amber-brand">
                                la tua sala
                            </span>
                            .
                        </h2>
                    </div>
                    <p className="text-sf-secondary text-lg leading-relaxed">
                        Dalle anagrafiche dei camerieri al calendario settimanale,
                        passando per KPI come scontrino medio, mance e puntualità.
                        Tutto in un'interfaccia disegnata per non rallentare il
                        servizio.
                    </p>
                </div>

                <div className="grid md:grid-cols-12 gap-5">
                    <FeatureCard
                        className="md:col-span-7 row-span-2 min-h-[320px]"
                        icon={Calendar}
                        title="Calendario turni intelligente"
                        text="Vista settimanale, drag-friendly. Assegna turni in pochi click, con ruoli e postazione."
                        accent
                    />
                    <FeatureCard
                        className="md:col-span-5"
                        icon={Users}
                        title="Anagrafica brigata"
                        text="Camerieri, chef, barman, runner. Tariffe orarie, contatti, stato."
                    />
                    <FeatureCard
                        className="md:col-span-5"
                        icon={LineChart}
                        title="Performance per persona"
                        text="Coperti, scontrino medio, mance, valutazioni e puntualità."
                    />
                    <FeatureCard
                        className="md:col-span-4"
                        icon={Coffee}
                        title="Brigata produttiva"
                        text="Analytics per ruolo: chi tira di più, dove ottimizzare."
                    />
                    <FeatureCard
                        className="md:col-span-4"
                        icon={Clock}
                        title="Ore pianificate"
                        text="Monitor settimanale di copertura, allerta turni scoperti."
                    />
                    <FeatureCard
                        className="md:col-span-4"
                        icon={Star}
                        title="Top performer"
                        text="Riconosci e motiva i migliori della settimana."
                    />
                </div>
            </section>

            {/* Brigade strip */}
            <section
                id="brigade"
                className="bg-navy text-white py-24 relative overflow-hidden"
            >
                <div className="bg-grid absolute inset-0 opacity-30"></div>
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-14 items-center relative">
                    <div>
                        <img
                            src={FEATURE_IMG}
                            alt="Cucina al lavoro"
                            className="rounded-2xl border border-white/10 w-full h-[380px] object-cover"
                        />
                    </div>
                    <div>
                        <div className="label-eyebrow text-amber-brand mb-3">
                            Produttività di brigata
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl mb-6 leading-tight text-white">
                            Misura quello che, fino a ieri,
                            <span className="italic text-amber-brand">
                                {" "}
                                vivevi solo
                            </span>{" "}
                            in sala.
                        </h2>
                        <p className="text-white/80 text-lg leading-relaxed mb-8">
                            Coperti serviti, scontrino medio, mance,
                            puntualità, copertura turni per giornata. Capisci
                            chi è il motore, chi va supportato, e dove
                            riorganizzare la brigata.
                        </p>
                        <div className="grid grid-cols-3 gap-6">
                            <Stat n="92%" label="Puntualità media" />
                            <Stat n="€34" label="Scontrino medio" />
                            <Stat n="−18%" label="Turni scoperti" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section
                id="pricing"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-24"
            >
                <div className="text-center mb-16 max-w-2xl mx-auto">
                    <div className="label-eyebrow mb-3">Prezzi</div>
                    <h2 className="font-display text-4xl md:text-5xl text-navy mb-4">
                        Semplice, mensile, senza vincoli.
                    </h2>
                    <p className="text-sf-secondary text-lg">
                        Inizia gratis. Passa a Base o Pro quando ti serve.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    <PricingCard
                        name="Base"
                        info={plans?.base}
                        fallbackPrice="9"
                        items={[
                            "Fino a 10 dipendenti",
                            "Calendario turni",
                            "Anagrafica brigata",
                            "Report base",
                            "Email support",
                        ]}
                        cta="/register"
                        ctaLabel="Inizia con Base"
                        testid="pricing-base"
                    />
                    <PricingCard
                        name="Pro"
                        info={plans?.pro}
                        fallbackPrice="19"
                        featured
                        items={[
                            "Dipendenti illimitati",
                            "Performance avanzate (coperti, scontrino, mance, rating)",
                            "Brigata productivity analytics",
                            "Export PDF/CSV",
                            "Multi-locale",
                            "Notifiche email",
                            "Support prioritario",
                        ]}
                        cta="/register"
                        ctaLabel="Inizia con Pro"
                        testid="pricing-pro"
                    />
                </div>
            </section>

            <footer className="border-t border-sf bg-white relative">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <Logo className="h-28 w-auto" />
                    <div className="text-sm text-sf-muted">
                        Staff Flow 360 è un prodotto di Makai · P.IVA
                        09364410969
                    </div>
                    <div className="text-xs text-sf-muted">
                        © {new Date().getFullYear()} Staff Flow. Made in Italy.
                    </div>
                </div>
                {/* Hidden Staff Admin access (bottom right) */}
                <Link
                    to="/sa-login"
                    title="Staff Admin"
                    aria-label="Staff Admin"
                    className="fixed bottom-3 right-3 z-50 w-8 h-8 rounded-full bg-navy/10 hover:bg-navy text-navy hover:text-amber-brand text-[10px] font-bold uppercase tracking-widest flex items-center justify-center transition-colors opacity-50 hover:opacity-100"
                    data-testid="sa-hidden-link"
                >
                    SA
                </Link>
            </footer>
        </div>
    );
}

function FeatureCard({ icon: Icon, title, text, className = "", accent }) {
    return (
        <div
            className={`relative bg-white border border-sf rounded-xl p-7 lift hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)] ${className}`}
        >
            <div
                className={`inline-flex items-center justify-center w-11 h-11 rounded-lg mb-5 ${accent ? "bg-amber-brand text-navy" : "bg-[#f0efeb] text-navy"}`}
            >
                <Icon size={20} />
            </div>
            <div className="font-display text-2xl text-navy mb-2">{title}</div>
            <div className="text-sf-secondary leading-relaxed">{text}</div>
        </div>
    );
}

function Stat({ n, label }) {
    return (
        <div>
            <div className="font-display text-4xl text-amber-brand">{n}</div>
            <div className="text-xs text-white/70 mt-1">{label}</div>
        </div>
    );
}

function PricingCard({ name, info, fallbackPrice, items, cta, ctaLabel, featured, testid }) {
    const basePrice = info?.price ?? parseFloat(fallbackPrice);
    const currentPrice = info?.current_price ?? basePrice;
    const promoActive = !!info?.promo_active;
    return (
        <div
            className={`relative rounded-2xl p-8 lift ${
                featured
                    ? "bg-navy text-white border-2 border-amber-brand shadow-2xl"
                    : "bg-white border border-sf shadow-sm"
            }`}
            data-testid={testid}
        >
            {featured && (
                <div className="absolute -top-3 left-8 bg-amber-brand text-navy text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Più scelto
                </div>
            )}
            {promoActive && (
                <div className="absolute -top-3 right-8 bg-[#d90429] text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1">
                    <Flame size={11} /> Promo
                </div>
            )}
            <div className={`label-eyebrow mb-2 ${featured ? "text-amber-brand" : ""}`}>
                Piano
            </div>
            <div className="font-display text-3xl mb-6">{name}</div>
            {promoActive ? (
                <div className="mb-3">
                    <div className="flex items-baseline gap-1">
                        <span className="font-display text-6xl">€{currentPrice.toFixed(currentPrice % 1 === 0 ? 0 : 2)}</span>
                        <span className={`${featured ? "text-white/70" : "text-sf-muted"} text-sm`}>
                            /mese
                        </span>
                    </div>
                    <div className={`mt-1 ${featured ? "text-white/60" : "text-sf-muted"}`}>
                        <span className="line-through text-lg">€{basePrice.toFixed(basePrice % 1 === 0 ? 0 : 2)}/mese</span>
                    </div>
                </div>
            ) : (
                <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-display text-6xl">€{basePrice.toFixed(basePrice % 1 === 0 ? 0 : 2)}</span>
                    <span className={`${featured ? "text-white/70" : "text-sf-muted"} text-sm`}>/mese</span>
                </div>
            )}
            {promoActive && info?.promo_ends_at && (
                <div className={`text-sm font-semibold mb-5 ${featured ? "text-amber-brand" : "text-[#d90429]"}`}>
                    Offerta valida fino al {fmtItDate(info.promo_ends_at)}
                </div>
            )}
            {!promoActive && <div className="mb-5" />}
            <ul className="space-y-3 mb-8 text-sm">
                {items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                        <Check
                            size={16}
                            className={`mt-0.5 flex-shrink-0 ${featured ? "text-amber-brand" : "text-navy"}`}
                        />
                        <span className={featured ? "text-white/90" : "text-sf-secondary"}>
                            {it}
                        </span>
                    </li>
                ))}
            </ul>
            <Link to={cta}>
                <Button
                    className={`w-full lift ${
                        featured
                            ? "bg-amber-brand text-navy hover:bg-amber-brand/90 font-bold"
                            : "bg-navy text-white hover:bg-navy/90"
                    }`}
                    data-testid={`${testid}-cta`}
                >
                    {ctaLabel}
                </Button>
            </Link>
        </div>
    );
}
